import React, { useEffect, useState } from 'react';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import styles from './EPrescriptionWizardPatientDataForm.module.scss';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import Text from '../../../../../Components/Base/Text/Text';
import FormControl from '../../../../../Components/Form/FormControl';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import InputField from '../../../../../Components/Form/components/InputField';
import SelectField from '../../../../../Components/Form/components/SelectField';
import PhoneNumberField from '../../../../../Components/PhoneNumber/PhoneNumberField';
import NumberField from '../../../../../Components/Form/components/NumberField';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import { useForm } from 'react-hook-form';
import moment, { Moment } from 'moment';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

export interface EPrescriptionWizardPatientDataFormDefaultValues {
	firstName?: string;
	lastName?: string;
	dateOfBirth?: Moment;
	insuranceNumber?: string;
	insuranceCompany?: SelectValue;
	insuranceType?: SelectValue;
	phoneNumber?: string;
	gender?: SelectValue | string;
	documentNumber?: number;
	idType?: SelectValue;
	weight?: number;
	street?: string;
	descriptiveNumber?: string;
	guideNumber?: number;
	evidenceNumber?: number;
	city?: string;
	postCode?: string;
	contactAddress?: string;
	country?: string;
	district?: string;
	electronicMail?: string;
	nameOfPrison?: string;
}

export interface EPrescriptionWizardPatientDataFormValues {
	firstName: string;
	lastName: string;
	dateOfBirth?: Moment;
	insuranceNumber: string;
	insuranceCompany: SelectValue;
	insuranceType?: SelectValue;
	phoneNumber?: string;
	gender?: SelectValue;
	documentNumber?: number;
	idType?: SelectValue;
	weight?: number;
	street: string;
	descriptiveNumber: string;
	guideNumber?: number;
	evidenceNumber?: number;
	city: string;
	postCode: string;
	contactAddress?: string;
	country?: string;
	district?: string;
	electronicMail?: string;
	nameOfPrison?: string;
}

interface Props {
	onSubmit: (values: EPrescriptionWizardPatientDataFormValues) => void;
	defaultValues?: EPrescriptionWizardPatientDataFormDefaultValues;
}

const INSURANCE_COMPANIES: SelectValue[] = [
	{ label: '213 - RBP', value: '213' },
	{ label: '201 - VoZP', value: '201' },
	{ label: '111 - VZP', value: '111' },
	{ label: '209 - ZPŠ', value: '209' },
	{ label: '211 - ZPMV', value: '211' },
	{ label: '205 - ČPZP', value: '205' },
	{ label: '207 - OZP', value: '207' },
	{ label: '999 - Samoplátce', value: '999' },
];

const INSURANCE_TYPES: SelectValue[] = [
	{ label: 'terms.suklDruhPojisteni.verejne', value: 'VEREJNE' },
	{
		label: 'terms.suklDruhPojisteni.smluvni',
		value: 'SMLUVNI_PRIPOJISTENI',
	},
	{
		label: 'terms.suklDruhPojisteni.cestovni',
		value: 'CESTOVNI_PRIPOJISTENI',
	},
	{
		label: 'terms.suklDruhPojisteni.pojisteniEu',
		value: 'POJISTENI_EU',
	},
];

const GENDERS: SelectValue[] = [
	{ label: 'base.man', value: 'muz' },
	{ label: 'base.woman', value: 'zena' },
];

const IDENTIFICATION_TYPES = [
	{ label: 'base.passport', value: 'P' },
	{ label: 'base.idCard', value: 'ID' },
	{ label: 'component.ePrescriptionPatientInformation.ix', value: 'IX' },
	{ label: 'component.ePrescriptionPatientInformation.ir', value: 'IR' },
];

const VALIDATION_SCHEMA = yup.object({
	firstName: yup.string().required('base.shared.firstName'),
	lastName: yup.string().required('base.shared.lastName'),
	dateOfBirth: yup
		.date()
		.notRequired()
		.typeError('validation.ePrescription.date')
		.nullable()
		.transform(value => (value ? value : null)),
	insuranceNumber: yup
		.string()
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	insuranceCompany: yup
		.object({
			label: yup.string(),
			value: yup.string(),
		})
		.required('')
		.typeError('validation.ePrescription.insuranceCompany'),
	insuranceType: yup
		.object({
			label: yup.string(),
			value: yup.string(),
		})
		.nullable()
		.typeError('validation.ePrescription.insuranceType'),
	phoneNumber: yup
		.string()
		.notRequired()
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	gender: yup
		.object({
			label: yup.string(),
			value: yup.string(),
		})
		.nullable(),
	documentNumber: yup
		.string()
		.notRequired()
		.min(1)
		.max(9)
		.nullable()
		.transform(value => (value ? value : null)),
	idType: yup
		.object({
			label: yup.string(),
			value: yup.string(),
		})
		.nullable()
		.typeError('validation.ePrescription.idType'),
	weight: yup
		.number()
		.notRequired()
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	street: yup
		.string()
		.required('validation.ePrescription.street.required')
		.min(1, 'validation.ePrescription.street.min')
		.max(48, 'validation.ePrescription.street.max'),
	descriptiveNumber: yup
		.string()
		.notRequired()
		.matches(/^[0-9a-zA-Z]{1,5}$/, 'validation.ePrescription.descriptiveNumber')
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	guideNumber: yup
		.number()
		.notRequired()
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	evidenceNumber: yup
		.number()
		.notRequired()
		.nullable()
		.transform(value => (value ? value : null))
		.typeError('validation.ePrescription.number'),
	city: yup
		.string()
		.required('validation.ePrescription.city.required')
		.min(1, 'validation.ePrescription.city.min')
		.max(48, 'validation.ePrescription.city.max'),
	postCode: yup
		.string()
		.required('validation.ePrescription.postCode.required')
		.matches(/^[0-9]{5}$/, 'base.shared.form.postCode.validationErrorMessage')
		.length(5, 'base.shared.form.postCode.validationErrorMessage')
		.nullable()
		.transform(value => (value ? value : null)),
	contactAddress: yup.string(),
	county: yup.string(),
	district: yup.string(),
	electronicMail: yup.string().email(),
	nameOfPrison: yup.string().nullable().max(200, 'base.error.prisonNameFormat'),
});

const EPrescriptionWizardPatientDataForm: React.FC<Props> = ({
	onSubmit,
	defaultValues,
}) => {
	const [isLeftColumnOpen, setLeftColumnOpen] = useState(false);
	const [isRightColumnOpen, setRightColumnOpen] = useState(false);

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(VALIDATION_SCHEMA),
	});

	useEffect(() => {
		formMethods.reset(defaultValues);
	}, [defaultValues]);

	const isSelfPayer = formMethods.watch('insuranceCompany')?.value === '999';

	const handleSave = async (
		values: EPrescriptionWizardPatientDataFormValues,
	) => {
		onSubmit(values);
	};

	return (
		<ScrollContainer className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<Text tx={'base.shared.patient'} className={styles.title} firstUpper />
				<div className='row'>
					<div className='col-md-6'>
						<InputField
							name='firstName'
							label='base.shared.firstName'
							isRequired
							isWrapped
						/>
						<InputField
							name='lastName'
							label='base.shared.lastName'
							isRequired
							isWrapped
						/>
						<FormControl
							label='base.shared.dateOfBirth'
							name='dateOfBirth'
							isRequired>
							<DatePicker maxDate={moment()} />
						</FormControl>
						<InputField
							label='base.shared.insuranceNumber'
							name='insuranceNumber'
							isRequired
							isWrapped
						/>
						<SelectField
							name='insuranceCompany'
							label='base.shared.insuranceCompany'
							options={INSURANCE_COMPANIES}
							isRequired
							isWrapped
						/>
						{!isSelfPayer && (
							<SelectField
								name='insuranceType'
								label='base.typeOfInsurance'
								options={INSURANCE_TYPES}
								isWrapped
							/>
						)}
						<PhoneNumberField
							name='phoneNumber'
							label='base.shared.phone'
							isWrapped
						/>
						{isLeftColumnOpen && (
							<>
								<SelectField
									name='gender'
									label='base.shared.gender'
									options={GENDERS}
									isWrapped
								/>
								<InputField
									name='documentNumber'
									label='base.documentNumber'
									isWrapped
								/>
								<SelectField
									name='idType'
									label='base.shared.type'
									options={IDENTIFICATION_TYPES}
									isWrapped
								/>
								<NumberField label='base.weight' name='weight' />
								<InputField
									name='electronicMail'
									label='base.electronicMail'
									isWrapped
								/>
							</>
						)}
						<LinkButton
							onClick={() => setLeftColumnOpen(prev => !prev)}
							theme={Theme.BLUE}
							title={isLeftColumnOpen ? 'base.showLess2' : 'base.showMore'}
						/>
					</div>
					<div className='col-md-6'>
						<InputField
							name='street'
							label='base.shared.street'
							isRequired
							isWrapped
						/>
						<NumberField
							label='base.shared.descriptiveNumber'
							name='descriptiveNumber'
							isRequired
							isWrapped
						/>
						{isRightColumnOpen && (
							<>
								<NumberField
									label='base.shared.guideNumber'
									name='guideNumber'
									isWrapped
								/>
								<NumberField
									label='base.registryNumber'
									name='evidenceNumber'
									isWrapped
								/>
							</>
						)}
						<InputField
							name='city'
							label='component.ePrescription.village'
							isRequired
							isWrapped
						/>
						<NumberField
							label='base.shared.postCode'
							name='postCode'
							isRequired
							isWrapped
						/>
						<InputField
							name='contactAddress'
							label='component.ePrescription.contactAddress'
							isWrapped
						/>
						{isRightColumnOpen && (
							<>
								<InputField
									name='county'
									label='component.ePrescription.district'
									isWrapped
								/>
								<InputField
									name='district'
									label='component.ePrescription.partOfVillage'
									isWrapped
								/>
								<InputField
									name='nameOfPrison'
									label='component.ePrescription.nameOfPrison'
									isWrapped
								/>
							</>
						)}
						<LinkButton
							onClick={() => setRightColumnOpen(prev => !prev)}
							theme={Theme.BLUE}
							title={isRightColumnOpen ? 'base.showLess2' : 'base.showMore'}
						/>
					</div>
				</div>
				<div className='d-flex mt-4 justify-content-between'>
					<SubmitButton
						title='base.shared.continue'
						onSubmit={values =>
							handleSave(
								values as unknown as EPrescriptionWizardPatientDataFormValues,
							)
						}
					/>
				</div>
			</FormWrapper>
		</ScrollContainer>
	);
};

export default EPrescriptionWizardPatientDataForm;
